import axios from "axios";
import React, { useEffect, useState } from "react";
import {
  FiShare2,
  FiCopy,
  FiGift,
  FiUsers,
  FiDollarSign,
  FiMessageSquare,
  FiSend,
  FiMail,
  FiCheckCircle,
  FiLoader,
  FiLink,
  FiStar,
  FiUserPlus,
  FiX,
  FiClipboard,
  FiZap,
  FiTrendingUp,
  FiShield,
  FiCheck,
  FiExternalLink,
} from "react-icons/fi";
import { FaWhatsapp, FaTelegramPlane } from "react-icons/fa";
import { toast } from "react-toastify";
import "../index.css";
import "./EmployeeDashboard.css";
import "./EmployeeLeaves.css";

const API_BASE_URL = "https://api.ingrainsystems.com/api";


const MyReferral = () => {
  const [referralCode, setReferralCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");

  const getClientId = () => {
    return localStorage.getItem("clientId") || "";
  };

  useEffect(() => {
    fetchReferralCode();
  }, []);

  const fetchReferralCode = async () => {
    try {
      setLoading(true);
      setError(null);
      const clientId = getClientId();

      if (!clientId) {
        setError("Client ID not found. Please login again.");
        setLoading(false);
        return;
      }

      const response = await axios.get(
        `${API_BASE_URL}/clients/get-referral-code/${clientId}`
      );

      if (response.data.success) {
        setReferralCode(response.data.referralCode);
      } else {
        setError(response.data.message || "Failed to fetch referral code");
      }
    } catch (err) {
      console.error("Error fetching referral code:", err);
      setError(err.response?.data?.message || "Failed to fetch referral code");
    } finally {
      setLoading(false);
    }
  };

  const getReferralLink = () => {
    return `${window.location.origin}/candidate-register?ref=${referralCode || ""}`;
  };

  const copyToClipboard = async () => {
    const link = getReferralLink();
    try {
      await navigator.clipboard.writeText(link);
      setCopiedLink(true);
      toast.success("Referral link copied to clipboard!");
      setTimeout(() => setCopiedLink(false), 2500);
    } catch (err) {
      toast.error("Failed to copy link");
    }
  };

  const copyCodeOnly = async () => {
    if (!referralCode) return;
    try {
      await navigator.clipboard.writeText(referralCode);
      setCopiedCode(true);
      toast.success("Referral code copied!");
      setTimeout(() => setCopiedCode(false), 2500);
    } catch (err) {
      toast.error("Failed to copy code");
    }
  };

  const shareOnWhatsApp = () => {
    const link = getReferralLink();
    const text = `🎉 Join me on Timely Health Care! Use my referral code: *${referralCode}* to register and get free bonus coins.\n\nSign up here: ${link}`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  const shareOnTelegram = () => {
    const link = getReferralLink();
    const text = `🎉 Join me on Timely Health Care! Use my referral code: ${referralCode} to sign up and get free reward coins.`;
    const url = `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  const shareByEmail = (recipient = "") => {
    const link = getReferralLink();
    const subject = "Invitation to join Timely Health Care";
    const body = `Hi,\n\nI am inviting you to join Timely Health Care recruitment portal.\n\nUse my referral code: ${referralCode} when registering.\n\nDirect registration link: ${link}\n\nJoin now and unlock exclusive hiring tools and bonus coins!`;
    const url = `mailto:${encodeURIComponent(recipient)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(url, "_blank");
  };

  const handleSendInvite = (e) => {
    e.preventDefault();
    if (!inviteEmail || !inviteEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    shareByEmail(inviteEmail);
    setInviteEmail("");
    toast.success("Email client opened with prefilled invitation!");
  };

  const shareNative = async () => {
    const link = getReferralLink();
    const shareData = {
      title: "Timely Health Care Referral",
      text: `Join me on Timely Health Care! Use my referral code: ${referralCode}`,
      url: link,
    };

    try {
      if (navigator.share) {
        setShareLoading(true);
        await navigator.share(shareData);
        toast.success("Shared successfully!");
      } else {
        copyToClipboard();
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        toast.error("Sharing was cancelled or failed");
      }
    } finally {
      setShareLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ backgroundColor: "white" }} className="emp-dash min-h-screen">
        <main className="p-4 sm:p-6 lg:p-8">
          <div className="py-24 text-center">
            <div className="flex flex-col items-center justify-center gap-3">
              <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin"></div>
              <span className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                Syncing referral data...
              </span>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ backgroundColor: "white" }} className="emp-dash min-h-screen">
        <main className="p-4 sm:p-6 lg:p-8">
          <div className="max-w-md mx-auto my-12 p-6 bg-rose-50 border border-rose-200 rounded-2xl text-center space-y-4">
            <div className="w-12 h-12 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center mx-auto">
              <FiX size={24} />
            </div>
            <div>
              <h3 className="text-base font-bold text-rose-900">Referral Error</h3>
              <p className="text-xs text-rose-700 mt-1">{error}</p>
            </div>
            <button
              onClick={fetchReferralCode}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold uppercase tracking-widest rounded-xl transition-all shadow-md shadow-indigo-600/20 cursor-pointer"
            >
              <FiShare2 size={14} /> Try Again
            </button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: "white" }} className="emp-dash">
      <main>
        {/* Page Header */}
        <div className="emp-dash__header">
          <div>
            <h1 className="emp-dash__greeting">
              Refer & <span>Earn</span>
            </h1>
            <p className="emp-dash__subtitle">
              Invite friends & companies to Timely Health and get 100 free coins for every signup.
            </p>
          </div>
          <div className="emp-dash__date-pill">
            <FiGift />
            <span>
              {new Date().toLocaleDateString("en-US", {
                weekday: "short",
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>
          </div>
        </div>

        {/* Main Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column (Span 2) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Referral Hero Card */}
            <div className="emp-dash__card p-6 relative overflow-hidden bg-gradient-to-br from-white via-indigo-50/20 to-violet-50/40">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                    <FiGift size={15} className="text-emerald-600" />
                  </div>
                  <h2 className="text-sm font-bold text-gray-800 uppercase tracking-widest">
                    Your Referral Code
                  </h2>
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700">
                  100 Coins / Ref
                </span>
              </div>

              {/* Gradient Banner */}
              <div className="p-6 md:p-8 rounded-2xl bg-gradient-to-r from-emerald-600 via-teal-600 to-indigo-700 text-white shadow-xl shadow-emerald-600/15 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full blur-3xl -mr-20 -mt-20 pointer-events-none" />
                <div className="relative flex flex-col md:flex-row items-center justify-between gap-6">
                  <div className="space-y-2 text-center md:text-left">
                    <p className="text-[10px] font-bold text-emerald-100 uppercase tracking-[0.2em]">
                      Unique Client Code
                    </p>
                    <div className="inline-flex items-center gap-3 bg-white/20 backdrop-blur-md px-5 py-2.5 rounded-2xl border border-white/30 shadow-inner">
                      <span className="text-2xl sm:text-3xl font-black tracking-widest text-white">
                        {referralCode || "------"}
                      </span>
                      <button
                        type="button"
                        onClick={copyCodeOnly}
                        className="p-2 rounded-xl bg-white text-emerald-700 hover:bg-emerald-50 active:scale-95 transition-all shadow-sm cursor-pointer"
                        title="Copy Code"
                      >
                        {copiedCode ? <FiCheck size={16} /> : <FiCopy size={16} />}
                      </button>
                    </div>
                    <p className="text-xs text-emerald-100/90 font-medium">
                      Share this code directly or send the registration link below.
                    </p>
                  </div>

                  {/* Highlight Pill */}
                  <div className="flex flex-col items-center justify-center p-4 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-center min-w-[140px]">
                    <div className="w-10 h-10 rounded-full bg-yellow-400 text-slate-900 flex items-center justify-center font-extrabold text-base mb-1 shadow-md">
                      ₹
                    </div>
                    <span className="text-lg font-black text-white">+100</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-100">
                      Coins Per Friend
                    </span>
                  </div>
                </div>
              </div>

              {/* One-click Link Copy Row */}
              <div className="mt-5 space-y-1.5">
                <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest">
                  Direct Referral Link
                </label>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1 group">
                    <FiLink
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-indigo-600 transition-colors"
                      size={15}
                    />
                    <input
                      type="text"
                      readOnly
                      value={getReferralLink()}
                      className="w-full pl-11 pr-4 py-3 bg-gray-50 rounded-xl border border-gray-200 text-xs font-medium text-gray-700 select-all outline-none"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={copyToClipboard}
                    className={`px-5 py-3 rounded-xl text-xs font-bold uppercase tracking-wider transition-all flex items-center gap-2 shadow-sm ${
                      copiedLink
                        ? "bg-emerald-600 text-white"
                        : "bg-indigo-600 hover:bg-indigo-700 text-white shadow-indigo-600/20"
                    }`}
                  >
                    {copiedLink ? <FiCheck size={14} /> : <FiCopy size={14} />}
                    {copiedLink ? "Copied!" : "Copy Link"}
                  </button>
                </div>
              </div>
            </div>

            {/* Share Channels Card */}
            <div className="emp-dash__card p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                  <FiShare2 size={15} className="text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-gray-800 uppercase tracking-widest">
                    Quick Share Channels
                  </h2>
                  <p className="text-[10px] text-gray-500">
                    Send invitations instantly via social & communication apps
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                {/* WhatsApp */}
                <button
                  type="button"
                  onClick={shareOnWhatsApp}
                  className="flex flex-col items-center justify-center p-4 rounded-xl border border-emerald-100 bg-emerald-50/40 hover:bg-emerald-50 hover:border-emerald-300 transition-all group cursor-pointer shadow-sm"
                >
                  <div className="w-12 h-12 rounded-xl bg-emerald-600 text-white flex items-center justify-center text-xl mb-2 group-hover:scale-110 transition-transform shadow-md shadow-emerald-600/20">
                    <FaWhatsapp />
                  </div>
                  <span className="text-xs font-bold text-gray-800">WhatsApp</span>
                  <span className="text-[10px] text-emerald-700 mt-0.5 font-medium">Instant Chat</span>
                </button>

                {/* Telegram */}
                <button
                  type="button"
                  onClick={shareOnTelegram}
                  className="flex flex-col items-center justify-center p-4 rounded-xl border border-sky-100 bg-sky-50/40 hover:bg-sky-50 hover:border-sky-300 transition-all group cursor-pointer shadow-sm"
                >
                  <div className="w-12 h-12 rounded-xl bg-sky-500 text-white flex items-center justify-center text-xl mb-2 group-hover:scale-110 transition-transform shadow-md shadow-sky-500/20">
                    <FaTelegramPlane />
                  </div>
                  <span className="text-xs font-bold text-gray-800">Telegram</span>
                  <span className="text-[10px] text-sky-700 mt-0.5 font-medium">Share Group</span>
                </button>

                {/* Email */}
                <button
                  type="button"
                  onClick={() => shareByEmail()}
                  className="flex flex-col items-center justify-center p-4 rounded-xl border border-indigo-100 bg-indigo-50/40 hover:bg-indigo-50 hover:border-indigo-300 transition-all group cursor-pointer shadow-sm"
                >
                  <div className="w-12 h-12 rounded-xl bg-indigo-600 text-white flex items-center justify-center text-xl mb-2 group-hover:scale-110 transition-transform shadow-md shadow-indigo-600/20">
                    <FiMail />
                  </div>
                  <span className="text-xs font-bold text-gray-800">Email</span>
                  <span className="text-[10px] text-indigo-700 mt-0.5 font-medium">Direct Mail</span>
                </button>

                {/* More / Native */}
                <button
                  type="button"
                  onClick={shareNative}
                  disabled={shareLoading}
                  className="flex flex-col items-center justify-center p-4 rounded-xl border border-gray-200 bg-gray-50/70 hover:bg-gray-100 hover:border-gray-300 transition-all group cursor-pointer shadow-sm"
                >
                  <div className="w-12 h-12 rounded-xl bg-gray-800 text-white flex items-center justify-center text-xl mb-2 group-hover:scale-110 transition-transform shadow-md shadow-gray-800/20">
                    {shareLoading ? <FiLoader className="animate-spin" /> : <FiShare2 />}
                  </div>
                  <span className="text-xs font-bold text-gray-800">More Apps</span>
                  <span className="text-[10px] text-gray-500 mt-0.5 font-medium">Native Share</span>
                </button>
              </div>
            </div>

            {/* How It Works Card */}
            <div className="emp-dash__card p-6">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center">
                  <FiTrendingUp size={15} className="text-violet-600" />
                </div>
                <div>
                  <h2 className="text-sm font-bold text-gray-800 uppercase tracking-widest">
                    How Referral Works
                  </h2>
                  <p className="text-[10px] text-gray-500">
                    3 simple steps to grow your wallet balance
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Step 1 */}
                <div className="p-4 rounded-2xl bg-indigo-50/40 border border-indigo-100 relative">
                  <div className="w-8 h-8 rounded-xl bg-indigo-600 text-white flex items-center justify-center font-bold text-xs mb-3 shadow-md shadow-indigo-600/20">
                    01
                  </div>
                  <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider">
                    Share Your Code
                  </h3>
                  <p className="text-[11px] text-gray-600 mt-1 leading-relaxed">
                    Send your exclusive link or referral code to friends, HRs, or fellow recruiters.
                  </p>
                </div>

                {/* Step 2 */}
                <div className="p-4 rounded-2xl bg-violet-50/40 border border-violet-100 relative">
                  <div className="w-8 h-8 rounded-xl bg-violet-600 text-white flex items-center justify-center font-bold text-xs mb-3 shadow-md shadow-violet-600/20">
                    02
                  </div>
                  <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider">
                    Friend Registers
                  </h3>
                  <p className="text-[11px] text-gray-600 mt-1 leading-relaxed">
                    They sign up on Timely Health Care using your code as their referrer.
                  </p>
                </div>

                {/* Step 3 */}
                <div className="p-4 rounded-2xl bg-emerald-50/40 border border-emerald-100 relative">
                  <div className="w-8 h-8 rounded-xl bg-emerald-600 text-white flex items-center justify-center font-bold text-xs mb-3 shadow-md shadow-emerald-600/20">
                    03
                  </div>
                  <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider">
                    Earn 100 Coins
                  </h3>
                  <p className="text-[11px] text-gray-600 mt-1 leading-relaxed">
                    Receive 100 free coins immediately in your wallet balance to post jobs!
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column (Span 1) */}
          <div className="space-y-6">
            {/* Quick Email Inviter Card */}
            <div className="emp-dash__card p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center">
                  <FiMail size={15} className="text-indigo-600" />
                </div>
                <h2 className="text-sm font-bold text-gray-800 uppercase tracking-widest">
                  Invite via Email
                </h2>
              </div>
              <p className="text-[10px] text-gray-500 mb-4 ml-10">
                Send an invitation directly to colleague's inbox
              </p>

              <form onSubmit={handleSendInvite} className="space-y-4">
                <div className="space-y-1.5">
                  <label className="block text-xs font-bold text-gray-700 uppercase tracking-widest">
                    Recipient's Email Address <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative group">
                    <FiMail
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-indigo-600 transition-colors"
                      size={15}
                    />
                    <input
                      type="email"
                      required
                      placeholder="colleague@company.com"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 focus:ring-4 focus:ring-indigo-600/5 focus:border-indigo-600 transition-all outline-none text-xs text-gray-800"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full flex items-center justify-center gap-2 py-3.5 px-6 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold uppercase tracking-wider transition-all shadow-lg shadow-indigo-600/20 cursor-pointer"
                >
                  <FiSend size={14} /> Send Email Invite
                </button>
              </form>
            </div>

            {/* Benefits Overview Card */}
            <div className="emp-dash__card p-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <FiDollarSign size={15} className="text-emerald-600" />
                </div>
                <h2 className="text-sm font-bold text-gray-800 uppercase tracking-widest">
                  Referral Rewards
                </h2>
              </div>

              <div className="space-y-3">
                <div className="p-3.5 bg-emerald-50/60 rounded-xl border border-emerald-100 flex items-start gap-3">
                  <FiCheckCircle size={15} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-bold text-emerald-950">Unlimited Referrals</p>
                    <p className="text-[10px] text-emerald-800 mt-0.5">
                      No limits on how many friends or companies you can invite.
                    </p>
                  </div>
                </div>

                <div className="p-3.5 bg-indigo-50/60 rounded-xl border border-indigo-100 flex items-start gap-3">
                  <FiZap size={15} className="text-indigo-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-bold text-indigo-950">Instant Coin Credit</p>
                    <p className="text-[10px] text-indigo-800 mt-0.5">
                      Rewards reflect in your{" "}
                      <a href="/mywallet" className="underline font-bold">
                        My Wallet
                      </a>{" "}
                      page without delay.
                    </p>
                  </div>
                </div>

                <div className="p-3.5 bg-violet-50/60 rounded-xl border border-violet-100 flex items-start gap-3">
                  <FiShield size={15} className="text-violet-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-bold text-violet-950">Lifetime Validity</p>
                    <p className="text-[10px] text-violet-800 mt-0.5">
                      Earned coins never expire and can be redeemed any time.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Tip matching PostNewJob */}
            <div className="p-4 bg-amber-50 rounded-2xl border border-amber-100 flex items-start gap-3">
              <FiZap size={15} className="text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-[11px] text-amber-900 font-medium leading-relaxed">
                Check your updated balance in{" "}
                <a href="/mywallet" className="font-bold text-indigo-600 hover:underline">
                  My Wallet
                </a>{" "}
                or explore promotional deals in{" "}
                <a href="/specialoffers" className="font-bold text-indigo-600 hover:underline">
                  Special Offers
                </a>
                .
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center gap-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest py-8">
          <FiClipboard className="text-indigo-500" />
          Last Updated: {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
        </div>
      </main>
    </div>
  );
};

export default MyReferral;